import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { LayoutDashboard, Users, ShoppingCart, BarChart3, Settings, Menu, LogOut, Shield, Brain, Mail, Webhook, Ticket, Handshake } from 'lucide-react';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin', exact: true },
  { label: 'Creators', icon: Users, path: '/admin/creators' },
  { label: 'Orders', icon: ShoppingCart, path: '/admin/orders' },
  { label: 'AI Usage', icon: Brain, path: '/admin/ai-usage' },
  { label: 'Email Blast', icon: Mail, path: '/admin/email-blast' },
  { label: 'Webhook Logs', icon: Webhook, path: '/admin/webhook-logs' },
  { label: 'Promo Codes', icon: Ticket, path: '/admin/promo-codes' },
  { label: 'Partnership', icon: Handshake, path: '/admin/partnership' },
];

function AdminSidebar({ onNavClick }) {
  return (
    <div className="flex flex-col h-full bg-[#0F3338]">
      <div className="p-6 pb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#FF9A00]" />
          <img src="/logos/funnely-white.png" alt="Funnely" className="h-6 object-contain" />
        </div>
        <p className="text-xs text-[#8AABAF] mt-1.5">Admin Panel</p>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
        {navItems.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={onNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${isActive ? 'bg-[#0F3338] text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-[#195259] flex items-center justify-center">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">Admin</p>
            <p className="text-xs text-slate-500 truncate">Platform Owner</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-[260px] flex-col shrink-0">
        <AdminSidebar />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px] bg-[#195259] border-none">
          <AdminSidebar onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 border-b bg-white flex items-center justify-between px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
            </Sheet>
            <span className="text-sm font-medium text-slate-500">Platform Administration</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#0F3338] text-white text-xs">A</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/app')}>
                <LayoutDashboard className="mr-2 h-4 w-4" /> Creator Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
